import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../redux/store";
import { moveInsectsToDeleted } from "../redux/slices/dataSlice";
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

interface ProbeDataSessionProps {
  sessionPageNum: number;
  setSessionPageNum: (page: number) => void;
  resetSessionPage: () => void;
}

export default function ProbeDataSession({ 
  sessionPageNum, 
  setSessionPageNum, 
  resetSessionPage 
}: ProbeDataSessionProps) {
  const { t, i18n } = useTranslation(['session', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const { sessionProbeData, userInfo } = useSelector((state: RootState) => state.data);
  const [navBtnDisable, setNavBtnDisable] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Get current locale for date formatting
  const getDateLocale = () => {
    return i18n.language === 'zh' ? 'zh-CN' : 'en-US';
  };

  // Fix hydration issues
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (sessionProbeData === null) {
      setNavBtnDisable(true);
    } else {
      setNavBtnDisable(false);
    }
    // Reset image error when session data changes
    setImageError(false);
  }, [sessionProbeData]);

  const handleDelete = () => {
    setShowConfirmation(true);
  };

  const confirmDelete = () => {
    if (sessionProbeData && sessionProbeData[sessionPageNum - 1]) {
      const msg = {
        sessionName: sessionProbeData[sessionPageNum - 1].session,
        trapId: sessionProbeData[sessionPageNum - 1].trapId,
      };
      dispatch(moveInsectsToDeleted(msg));
      setShowConfirmation(false);
      resetSessionPage();
      alert(t('deleteSuccess'));
    }
  };

  const cancelDelete = () => {
    setShowConfirmation(false);
  };

  const handlePrevious = () => {
    if (sessionPageNum > 1) {
      setSessionPageNum(sessionPageNum - 1);
    }
  };

  const handleNext = () => {
    if (sessionProbeData && sessionPageNum < sessionProbeData.length) {
      setSessionPageNum(sessionPageNum + 1);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(getDateLocale());
  };

  const convertTemperature = (temp: number) => {
    if (userInfo?.fahrenheit) {
      return ((temp * 9/5) + 32).toFixed(1) + '°F';
    }
    return temp.toFixed(1) + '°C';
  };

  // Prevent hydration mismatch
  if (!mounted) {
    return (
      <div className="flex items-center justify-center h-full">
        <h3 className="text-gray-800 text-lg">{t('common:loading')}</h3>
      </div>
    );
  }

  if (!sessionProbeData || sessionProbeData.length === 0 || !sessionProbeData[sessionPageNum - 1]) {
    return (
      <div className="flex items-center justify-center h-full">
        <h3 className="text-gray-800 text-lg">{t('selectSessionToView')}</h3>
      </div>
    );
  }

  const currentData = sessionProbeData[sessionPageNum - 1];

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Top Section - Image and Navigation */}
      <div className="flex flex-col items-center mb-6">
        <div className="relative w-80 h-64 mb-4 rounded-lg overflow-hidden border border-gray-300">
          {currentData.originalFileUrl && !imageError ? (
            <img
              src={currentData.originalFileUrl}
              alt={t('sessionCapture')}
              className="w-full h-full object-cover"
              onError={() => {
                logger.error("Failed to load image:", currentData.originalFileUrl);
                setImageError(true);
              }}
              onLoad={() => setImageError(false)}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-800">
                {imageError ? t('failedToLoadImage') : t('noImageAvailable')}
              </span>
            </div>
          )}
        </div>

        {/* Page Navigation */}
        <div className="flex items-center space-x-4">
          <button
            onClick={handlePrevious}
            disabled={navBtnDisable || sessionPageNum <= 1}
            className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600 cursor-pointer"
          >
            {t('prev')}
          </button>
          <span className="text-gray-900 font-medium">
            {sessionPageNum} / {sessionProbeData.length}
          </span>
          <button
            onClick={handleNext}
            disabled={navBtnDisable || sessionPageNum >= sessionProbeData.length}
            className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600 cursor-pointer"
          >
            {t('next')}
          </button>
        </div>
      </div>

      {/* Bottom Section - Data */}
      <div className="flex-1 grid grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900">{t('trapId')}</h4>
            <p className="text-lg text-gray-900">{currentData.trapId}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900">{t('building')}</h4>
            <p className="text-lg text-gray-900">{currentData.building}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900">{t('location')}</h4>
            <p className="text-lg text-gray-900">{currentData.location}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900">{t('dateTime')}</h4>
            <p className="text-lg text-gray-900">{formatDateTime(currentData.createdAt)}</p>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900">{t('totalCount')}</h4>
            <p className="text-lg text-gray-900">{currentData.particleCount}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900">{t('mothCount')}</h4>
            <p className="text-lg text-gray-900">{currentData.insectCollection.moth}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900">{t('beetleCount')}</h4>
            <p className="text-lg text-gray-900">{currentData.insectCollection.beetle}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900">{t('weevilCount')}</h4>
            <p className="text-lg text-gray-900">{currentData.insectCollection.weevil}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900">{t('otherCount')}</h4>
            <p className="text-lg text-gray-900">{currentData.insectCollection.other}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900">{t('temperature')}</h4>
            <p className="text-lg text-gray-900">{convertTemperature(currentData.temperature)}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900">{t('humidity')}</h4>
            <p className="text-lg text-gray-900">{currentData.humidity}%</p>
          </div>
        </div>
      </div>

      {/* Delete Button */}
      <div className="mt-6 text-center">
        <button
          onClick={handleDelete}
          className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors cursor-pointer"
        >
          {t('deleteSessionAction')}
        </button>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('confirmSessionDeletion')}
            </h3>
            <p className="text-gray-900 mb-6">
              {t('deleteConfirmFull')}
            </p>
            <div className="flex space-x-4">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 transition-colors cursor-pointer"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors cursor-pointer"
              >
                {t('common:delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
